import type { ParseKeys } from 'i18next';
import type {
  DiagnosisResult,
  InterVolumeOverlap,
  OverlapTreatment,
} from '@/api/generated/types.gen';
import type { Tone } from '@/lib/statusTone';

/** What a volume overlap is, not only that it exists (ENG-2474). */
export type OverlapKind = NonNullable<InterVolumeOverlap['kind']>;

/**
 * One pair of bodies that intersect, and how many places they do it in.
 *
 * `key` is the identity the viewer focuses on — the rail hands it back
 * unchanged rather than re-deriving it, so the two sides cannot disagree about
 * what "this pair" means.
 */
export interface OverlapPair {
  a: number;
  b: number;
  key: string;
  /** Intersection regions the B-rep check found between these two bodies. */
  regions: number;
  /**
   * The worst of the pair's regions: `interference` if any one is, else
   * `coincident`; `null` on a diagnosis older than the classification, which
   * says nothing rather than guessing at a hair.
   */
  kind: OverlapKind | null;
  /** Deepest penetration across the pair's regions, or null when unmeasured. */
  maxPenetrationMm: number | null;
}

/** The stable id for a body pair, order-independent. */
export function overlapPairKey(a: number, b: number): string {
  return a <= b ? `${a}:${b}` : `${b}:${a}`;
}

/**
 * Which bodies intersect, for the volume-overlap finding (ENG-2470).
 *
 * The finding has always been able to say WHERE the intersections are — the
 * eye toggle paints a point per overlap — and never which parts they belong
 * to. `body_index_a` / `body_index_b` are the answer, in the numbering the
 * Bodies panel and the fastener list already speak.
 *
 * **A record without them is skipped, not guessed at.** The backend leaves
 * them null when it cannot resolve the solid to a body with certainty, and the
 * whole reason that field is nullable is ENG-2121: the older
 * `volume_tag_a`/`volume_tag_b` index a different kernel's enumeration, and
 * assuming the two agree named the right body 1 time in 13 on a real
 * assembly. A pair rendered from the tag would be confidently wrong, which is
 * worse than the blank this returns.
 *
 * Pairs are deduplicated and ordered: a bolt through a flange intersects it
 * along the whole shank, so the check reports a region per intersection and
 * the same two bodies would otherwise fill the list. The regions are counted
 * instead, and the pair takes the worst kind and the deepest penetration of
 * them (ENG-2474) — one stud oversize in its hole makes the pair an
 * interference however many of its other contacts are clean.
 */
export function overlapPairs(
  result: Pick<DiagnosisResult, 'inter_volume_overlaps'> | null | undefined,
): OverlapPair[] {
  const byKey = new Map<string, OverlapPair>();
  for (const overlap of result?.inter_volume_overlaps ?? []) {
    const a = overlap.body_index_a;
    const b = overlap.body_index_b;
    if (a == null || b == null) continue;
    const key = overlapPairKey(a, b);
    const kind = overlap.kind ?? null;
    const penetration = overlap.penetration_mm ?? null;
    const found = byKey.get(key);
    if (found) {
      found.regions += 1;
      found.kind = worseKind(found.kind, kind);
      found.maxPenetrationMm = maxNullable(found.maxPenetrationMm, penetration);
      continue;
    }
    byKey.set(key, {
      a: Math.min(a, b),
      b: Math.max(a, b),
      key,
      regions: 1,
      kind,
      maxPenetrationMm: penetration,
    });
  }
  return [...byKey.values()].sort((x, y) => x.a - y.a || x.b - y.b);
}

function worseKind(
  x: OverlapKind | null,
  y: OverlapKind | null,
): OverlapKind | null {
  if (x === 'interference' || y === 'interference') return 'interference';
  return x ?? y;
}

function maxNullable(x: number | null, y: number | null): number | null {
  if (x == null) return y;
  if (y == null) return x;
  return Math.max(x, y);
}

/**
 * A penetration depth in the unit a reader can compare it in.
 *
 * Below a millimetre the number is given in micrometres: the whole point of
 * the depth is to tell a 3 µm hair from a 700 µm stud, and both read as
 * `0.00x mm` at the precision a rail column has room for. Three significant
 * figures either way — a depth is a measurement, and `0.718` says more than
 * `0.72` about a lens the CAD drew at 0.7.
 */
export function penetrationDisplay(mm: number): {
  value: string;
  unit: 'mm' | 'um';
} {
  const unit = mm < 1 ? 'um' : 'mm';
  const scaled = unit === 'um' ? mm * 1000 : mm;
  return {
    value: scaled.toLocaleString(undefined, { maximumSignificantDigits: 3 }),
    unit,
  };
}

/**
 * How many overlap records carry no body identity.
 *
 * Reported rather than swallowed: on an assembly where every record is
 * unresolved the finding shows its count and no bodies, and a reader deserves
 * to know that is a limit of the measurement rather than a UI that forgot.
 */
export function unresolvedOverlapCount(
  result: Pick<DiagnosisResult, 'inter_volume_overlaps'> | null | undefined,
): number {
  return (result?.inter_volume_overlaps ?? []).filter(
    (o) => o.body_index_a == null || o.body_index_b == null,
  ).length;
}

/** The two body indices behind an `overlapPairKey`, or null if it is malformed. */
export function parseOverlapPairKey(
  key: string | null | undefined,
): [number, number] | null {
  if (!key) return null;
  const parts = key.split(':');
  if (parts.length !== 2) return null;
  const [a, b] = parts;
  const bodyA = Number(a);
  const bodyB = Number(b);
  // Strict, because a key this function did not build is a bug, and resolving
  // one to a body anyway would paint an unrelated part of the model — which
  // looks like an answer. `Number` alone is far too generous: it reads '' as
  // 0, ' 1' as 1, and '1e3' as 1000. Body indices are non-negative integers
  // written in decimal, so require exactly that, spelled the same way back.
  if (String(bodyA) !== a || String(bodyB) !== b) return null;
  if (!Number.isInteger(bodyA) || !Number.isInteger(bodyB)) return null;
  if (bodyA < 0 || bodyB < 0) return null;
  return [bodyA, bodyB];
}

// ---------------------------------------------------------------------------
// Per-pair treatment (ENG-2475). What to DO about a pair, chosen on its
// sub-row and sent as one batch through the simplify endpoint.
// ---------------------------------------------------------------------------

/** One OCC treatment of a pair: cut the lens, fragment, fuse, or exclude. */
export type OverlapTreatmentOp = OverlapTreatment['op'];

/** Every op, in the order the menu lists them (the backend's batch order). */
export const OVERLAP_TREATMENT_OPS: readonly OverlapTreatmentOp[] = [
  'cut',
  'fragment',
  'fuse',
  'exclude',
];

/**
 * The user-facing name of each op, as an i18n key. One map for the row's
 * menu and the approval panel's outcome lines, so a treatment is offered and
 * reported in the same words — `Cut` never comes back as `cut`.
 */
export const OVERLAP_OP_LABEL_KEY = {
  cut: 'diagnosisComponents.inspectorStrip.overlapOpCut',
  fragment: 'diagnosisComponents.inspectorStrip.overlapOpFragment',
  fuse: 'diagnosisComponents.inspectorStrip.overlapOpFuse',
  exclude: 'diagnosisComponents.inspectorStrip.overlapOpExclude',
} as const satisfies Record<OverlapTreatmentOp, ParseKeys>;

/**
 * Whether an untyped op is one the menu offers. A proposal's outcome lines
 * are `dict[str, Any]` on the wire, so their `op` arrives as a string; an op
 * this build does not know is shown verbatim rather than mislabelled.
 */
export function isOverlapTreatmentOp(op: unknown): op is OverlapTreatmentOp {
  return (
    typeof op === 'string' &&
    (OVERLAP_TREATMENT_OPS as readonly string[]).includes(op)
  );
}

/**
 * The op a pair's kind suggests: an interference has material to remove, so
 * `cut`; a coincident face wants to be one conformal face, so `fragment`. A
 * pair with no measured kind (a diagnosis older than ENG-2474) gets `null` —
 * the user must choose, because defaulting either way would be the confident
 * guess the classification exists to replace.
 */
export function defaultOverlapOp(
  kind: OverlapKind | null,
): OverlapTreatmentOp | null {
  if (kind === 'interference') return 'cut';
  if (kind === 'coincident') return 'fragment';
  return null;
}

/** `body_index → volume_mm3` from the diagnosis's volume descriptors. */
export function bodyVolumes(
  result: Pick<DiagnosisResult, 'volume_descriptors'> | null | undefined,
): ReadonlyMap<number, number> {
  const out = new Map<number, number>();
  for (const v of result?.volume_descriptors ?? []) {
    if (typeof v.volume_mm3 === 'number') out.set(v.body_index, v.volume_mm3);
  }
  return out;
}

/**
 * Which body a cut takes the lens from by default: the LARGER of the pair.
 * A stud stays a stud; the plate gains a clean bore. This is a physics call,
 * so the row shows it and offers a swap rather than sending it silently. The
 * same rule the backend applies when the request names no loser.
 *
 * `null` when either volume is unknown — the row then cannot say who loses,
 * and the request leaves the choice to the backend rather than inventing one.
 */
export function defaultCutLoser(
  pair: Pick<OverlapPair, 'a' | 'b'>,
  volumes: ReadonlyMap<number, number>,
): number | null {
  const va = volumes.get(pair.a);
  const vb = volumes.get(pair.b);
  if (va == null || vb == null) return null;
  return vb > va ? pair.b : pair.a;
}

/** The other body of the pair — what a swap turns the loser into. */
export function otherBody(
  pair: Pick<OverlapPair, 'a' | 'b'>,
  body: number,
): number {
  return body === pair.a ? pair.b : pair.a;
}

/**
 * The request line for one pair. The loser only travels with a cut: the
 * endpoint rejects `cut_loser_body_index` on any other op, so a loser left
 * over from an earlier cut choice must not leak when the op changes.
 */
export function overlapTreatmentRequest(
  pair: Pick<OverlapPair, 'a' | 'b'>,
  op: OverlapTreatmentOp,
  cutLoser: number | null,
): OverlapTreatment {
  const treatment: OverlapTreatment = {
    body_index_a: pair.a,
    body_index_b: pair.b,
    op,
  };
  if (op === 'cut' && cutLoser != null) {
    treatment.cut_loser_body_index = cutLoser;
  }
  return treatment;
}

// ---------------------------------------------------------------------------
// The user's choices per pair, and what they resolve to (ENG-2501). Kept in
// the store rather than the rail so the viewer can colour a pair by its op
// and the inspector strip can count the ticks; every reader resolves a
// choice against the same defaults through the two `effective*` helpers.
// ---------------------------------------------------------------------------

/**
 * What the user has said about one pair. Every field is optional: an absent
 * one means "the default", so a pair the user never touched reads its op
 * from its kind and its loser from the volumes — and a re-diagnosis that
 * changes either changes what the row proposes.
 */
export interface OverlapPairChoice {
  selected?: boolean;
  op?: OverlapTreatmentOp;
  cutLoserBodyIndex?: number;
}

/** `overlapPairKey → choice`, the shape the store holds. */
export type OverlapPairChoices = ReadonlyMap<string, OverlapPairChoice>;

/**
 * One pair's choice merged over what it already had (ENG-2501). A new Map
 * each write, because the store slice is read through reference equality;
 * the other pairs' entries are shared, not copied. Pure, so the store and
 * the story harness that keeps its own choices merge the same way.
 */
export function withOverlapPairChoice(
  choices: OverlapPairChoices,
  key: string,
  patch: OverlapPairChoice,
): OverlapPairChoices {
  const next = new Map(choices);
  next.set(key, { ...choices.get(key), ...patch });
  return next;
}

/** The op a pair will be treated with: the user's, else its kind's default. */
export function effectiveOverlapOp(
  pair: Pick<OverlapPair, 'kind'>,
  choice: OverlapPairChoice | undefined,
): OverlapTreatmentOp | null {
  return choice?.op ?? defaultOverlapOp(pair.kind);
}

/** The body a cut takes the lens from: the user's swap, else the larger. */
export function effectiveCutLoser(
  pair: Pick<OverlapPair, 'a' | 'b'>,
  choice: OverlapPairChoice | undefined,
  volumes: ReadonlyMap<number, number>,
): number | null {
  return choice?.cutLoserBodyIndex ?? defaultCutLoser(pair, volumes);
}

/** Why a batch cannot be submitted yet, or null when it can. */
export type OverlapTreatBlocker = 'nothingSelected' | 'missingOp';

/** The i18n key for each blocker — one map for the rail and the strip. */
export const OVERLAP_TREAT_BLOCKER_KEY = {
  nothingSelected: 'diagnosisComponents.inspectorStrip.overlapTreatNone',
  missingOp: 'diagnosisComponents.inspectorStrip.overlapTreatChooseOp',
} as const satisfies Record<OverlapTreatBlocker, ParseKeys>;

export interface OverlapTreatmentBatch {
  /** The ticked pairs, in list order. */
  selected: OverlapPair[];
  /** One request line per ticked pair that has an op. */
  treatments: OverlapTreatment[];
  /** Nothing ticked, or a ticked pair with no op to send. */
  blocker: OverlapTreatBlocker | null;
}

/**
 * The batch the ticks make. Computed by the rail for its button and by the
 * workspace for the inspector strip's, from the same store slice, so the two
 * buttons can never disagree about what "Treat 3" would send.
 */
export function overlapTreatmentBatch(
  pairs: readonly OverlapPair[],
  choices: OverlapPairChoices,
  volumes: ReadonlyMap<number, number>,
): OverlapTreatmentBatch {
  const selected = pairs.filter((p) => choices.get(p.key)?.selected === true);
  const treatments: OverlapTreatment[] = [];
  let missingOp = false;
  for (const pair of selected) {
    const choice = choices.get(pair.key);
    const op = effectiveOverlapOp(pair, choice);
    if (op === null) {
      missingOp = true;
      continue;
    }
    treatments.push(
      overlapTreatmentRequest(
        pair,
        op,
        effectiveCutLoser(pair, choice, volumes),
      ),
    );
  }
  return {
    selected,
    treatments,
    blocker:
      selected.length === 0
        ? 'nothingSelected'
        : missingOp
          ? 'missingOp'
          : null,
  };
}

/**
 * What became of one per-pair treatment (the backend's
 * `OverlapTreatmentStatus`): `applied` changed geometry, `recorded` is an
 * exclude (no geometry, by design), `skipped` never reached the kernel,
 * `failed` did and could not complete.
 */
export type OverlapOutcomeStatus =
  | 'applied'
  | 'recorded'
  | 'skipped'
  | 'failed';

/**
 * The status tone of each outcome — ONE rule for the approval panel's text
 * and the viewer's paint of the hovered pair, so a line that reads green
 * never paints amber. `applied` changed geometry (success); `recorded` is an
 * exclude, touched but unchanged (muted); `skipped` and `failed` both mean
 * the pair was not treated and carry the backend's reason (warning).
 *
 * Narrowed past `diagnosis`: that tone is the product's identity, not a
 * status, and the renderer's palette has no colour for it.
 */
export const OVERLAP_OUTCOME_TONE: Record<
  OverlapOutcomeStatus,
  Exclude<Tone, 'diagnosis'>
> = {
  applied: 'success',
  recorded: 'muted',
  skipped: 'warning',
  failed: 'warning',
};
